import React, { useRef, useEffect, useState } from 'react';
import { Box, Button, Slider, ToggleButton, ToggleButtonGroup, Typography, Collapse, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MarkerIcon from '../../../../assets/icons/Marker.svg';
import HighlighterIcon from '../../../../assets/icons/Highlighter.svg';
import SprayIcon from '../../../../assets/icons/Spray.svg';
import TextIcon from '../../../../assets/icons/Text.svg';
import EraserIcon from '../../../../assets/icons/Eraser.svg';
import useIsLandscape from '../../../UIControls/useIsLandscape';

const WhiteboardToolbar = ({
    activeTool,
    setActiveTool,
    color,
    setColor,
    thickness,
    setThickness,
    onSave,
    onLoad,
    onReset,
    hasUnsavedChanges,
    isExpanded,
    setIsExpanded,
    recentColours = [],
    onPickRecentColour,
    showTextHelp = true,
}) => {
    const containerRef = useRef();
    const previousColorRef = useRef(color);
    const drawingThicknessRef = useRef(10); // Shared between pencil, highlighter, spray
    const textThicknessRef = useRef(5); // only for text
    const isLandscape = useIsLandscape();
    const [isTextHelpOpen, setIsTextHelpOpen] = useState(false);

    // When activeTool changes, enforce:
    // - If highlighter: force the color to 'rgba(220,240,60,0.5)' and save the current (base) color.
    // - If leaving highlighter: if the current color is still the highlighter's color, restore the base color.
    useEffect(() => {
        if (activeTool === 'highlighter') {
            // Save the current base color if it’s not already the highlighter color.
            if (color !== 'rgba(220,240,60,0.5)') {
                previousColorRef.current = color;
            }
            // Force highlighter color.
            setColor('rgba(220,240,60,0.5)');
        } else {
            // If we're not in highlighter and the color is still the forced yellow,
            // restore the base color.
            if (color === 'rgba(220,240,60,0.5)' && previousColorRef.current) {
                setColor(previousColorRef.current);
            }
        }
        // Remember & restore thickness for drawing tools vs text
        if (['pencil', 'highlighter', 'spray'].includes(activeTool)) {
            setThickness(drawingThicknessRef.current);
        } else if (activeTool === 'text') {
            setThickness(textThicknessRef.current);
        }
    }, [activeTool, setColor, color, setThickness]);

    useEffect(() => {}, [isLandscape]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isClickInsideToolbar = containerRef.current?.contains(event.target);
            const isCanvasClick =
                event.target?.closest('.whiteboard-canvas-wrapper') || event.target?.closest('.whiteboard-container');

            if (!isClickInsideToolbar && !isCanvasClick) {
                setIsExpanded(false); // only collapse if not interacting with canvas or toolbar
            }
        };

        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isExpanded, setIsExpanded]);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: isLandscape ? '100%' : 260,
            }}
        >
            {isExpanded ? (
                <Box
                    className="gradient-border-wrapper"
                    sx={{
                        position: 'sticky',
                        top: isLandscape ? '0px' : '100px',
                        zIndex: 999,
                        borderRadius: '16px',
                        background: 'linear-gradient(132deg, #fa96c3 -2.07%, #ff669b 15.82%, #bd8de8 116.64%)',
                        padding: '2px',
                        display: 'inline-block',
                        width: isLandscape ? '95%' : 'auto',
                    }}
                >
                    <Box
                        className="whiteboard-toolbar-container"
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            borderRadius: '14px',
                            padding: '16px',
                            width: isLandscape ? '100%' : '260px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                            display: 'flex',
                            flexDirection: isLandscape ? 'row' : 'column',
                            gap: isLandscape ? 1 : 2,
                            alignItems: 'center',
                        }}
                    >
                        {/* Toolbar Section for Tool Icons */}
                        <Box
                            className="toolbar-section gradient-icon-background"
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                borderRadius: '8px',
                                padding: '6px 5px',
                                background: 'linear-gradient(132deg, #fa96c3 -2.07%, #ff669b 15.82%, #bd8de8 116.64%)',
                                marginBottom: '16px',
                                gap: '15px',
                                flexWrap: 'nowrap',
                            }}
                        >
                            <ToggleButtonGroup
                                value={activeTool}
                                exclusive
                                onChange={(e, newTool) => {
                                    if (!newTool) return;

                                    if (['pencil', 'highlighter', 'spray'].includes(activeTool)) {
                                        drawingThicknessRef.current = thickness;
                                    } else if (activeTool === 'text') {
                                        textThicknessRef.current = thickness;
                                    }

                                    setActiveTool(newTool);
                                }}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '9px',
                                    background: 'transparent',
                                    padding: 0,
                                }}
                            >
                                {[
                                    { value: 'pencil', icon: MarkerIcon, alt: 'Pencil' },
                                    { value: 'highlighter', icon: HighlighterIcon, alt: 'Highlighter' },
                                    { value: 'spray', icon: SprayIcon, alt: 'Spray' },
                                    { value: 'text', icon: TextIcon, alt: 'Text' },
                                    { value: 'eraser', icon: EraserIcon, alt: 'Eraser' },
                                ].map((tool) => (
                                    <ToggleButton
                                        key={tool.value}
                                        value={tool.value}
                                        sx={{
                                            background: 'transparent',
                                            border: 'none',
                                            borderRadius: 2,
                                            padding: '6px',
                                            minWidth: 'auto',
                                            '&.Mui-selected': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                            },
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                            },
                                        }}
                                        onClick={() => {
                                            setActiveTool(tool.value);
                                            setIsExpanded(true);
                                        }}
                                    >
                                        <img src={tool.icon} alt={tool.alt} style={{ width: 24, height: 24 }} />
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>

                        {/* Colour + Thickness + Buttons (Responsive Row/Column Layout) */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: isLandscape ? 'row' : 'column',
                                gap: 2,
                                width: '100%',
                                alignItems: 'stretch',
                                justifyContent: 'space-between',
                            }}
                        >
                            {/* Colour & Thickness */}
                            <Box sx={{ display: 'flex', flex: 1, gap: 2 }}>
                                {/* Colour Picker */}
                                <Box
                                    className="color-picker"
                                    sx={{
                                        marginTop: '0px !important',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            textAlign: 'left',
                                            fontWeight: 400,
                                            mr: 3,
                                        }}
                                    >
                                        Colour:
                                    </Typography>

                                    <Box
                                        sx={{
                                            width: '48.33px',
                                            height: '18.32px',
                                            border: '1.9px solid #ECECEC',
                                            borderRadius: '3.16px',
                                            padding: 0,
                                            position: 'relative',
                                            overflow: 'hidden',
                                            mt: '6px',
                                        }}
                                    >
                                        <input
                                            type="color"
                                            value={activeTool === 'highlighter' ? '#DCF03C' : color}
                                            onChange={(e) => {
                                                if (activeTool !== 'highlighter') {
                                                    setColor(e.target.value);
                                                    previousColorRef.current = e.target.value;
                                                }
                                            }}
                                            disabled={activeTool === 'highlighter'}
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                border: 'none',
                                                borderRadius: 0,
                                                padding: 0,
                                                margin: 0,
                                                background: 'transparent',
                                                appearance: 'none',
                                                WebkitAppearance: 'none',
                                                MozAppearance: 'none',
                                                cursor: 'pointer',
                                                mt: 2,
                                            }}
                                        />
                                    </Box>
                                    {/* Recent colours (per-tool), hidden for highlighter/eraser */}
                                    {['pencil', 'spray'].includes(activeTool) && recentColours?.length > 0 && (
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                            {recentColours.map((c) => (
                                                <Box
                                                    key={`${activeTool}-${c}`}
                                                    onClick={() => onPickRecentColour?.(c)}
                                                    title={c}
                                                    sx={{
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: '3px',
                                                        border: '1px solid #ECECEC',
                                                        cursor: 'pointer',
                                                        backgroundColor: c,
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>

                                {/* Thickness */}
                                <Box sx={{ width: '100%' }}>
                                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'left' }}>
                                        Thickness:
                                    </Typography>
                                    <Slider
                                        value={thickness}
                                        onChange={(e, newValue) => {
                                            setThickness(newValue);
                                            if (['pencil', 'highlighter', 'spray'].includes(activeTool)) {
                                                drawingThicknessRef.current = newValue;
                                            } else if (activeTool === 'text') {
                                                textThicknessRef.current = newValue;
                                            }
                                        }}
                                        min={1}
                                        max={20}
                                        padding={0}
                                    />
                                </Box>
                            </Box>

                            {/* Action Buttons */}
                            {/* <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    justifyContent: isLandscape ? 'flex-end' : 'space-between',
                                    flex: isLandscape ? '0 0 auto' : '1',
                                    width: isLandscape ? 'auto' : '100%',
                                    height: isLandscape ? '40px' : 'auto',
                                    alignSelf: isLandscape ? 'auto' : 'auto',
                                }}
                            > */}
                            {/* Actions: Buttons + Help toggle/collapse (kept together so the help text aligns with the "Load" button) */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: isLandscape ? '0 0 auto' : '1',
                                    width: isLandscape ? 'auto' : '100%',
                                    gap: 1,
                                }}
                            >
                                {/* Buttons row */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1,
                                        justifyContent: isLandscape ? 'flex-end' : 'space-between',
                                        width: '100%',
                                        height: isLandscape ? 40 : 'auto',
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={onLoad}
                                        className="gradient-border-button"
                                        sx={{
                                            border: '1px solid rgb(255, 102, 155)',
                                            color: 'rgb(255, 102, 155)',
                                            fontWeight: 500,
                                            borderRadius: '8px',
                                        }}
                                    >
                                        Load
                                    </Button>
                                    <Button variant="contained" onClick={onSave} disabled={!hasUnsavedChanges}>
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={onReset}
                                        className="gradient-border-button"
                                        sx={{
                                            border: '1px solid rgb(255, 102, 155)',
                                            color: 'rgb(255, 102, 155)',
                                            fontWeight: 500,
                                            borderRadius: '8px',
                                        }}
                                    >
                                        Reset
                                    </Button>
                                </Box>
                                {/* Help toggle (left-aligned with the Load button) */}
                                {showTextHelp && activeTool === 'text' && (
                                    <>
                                        <Box
                                            onClick={() => setIsTextHelpOpen((v) => !v)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                fontFamily: 'Inter, sans-serif',
                                                fontWeight: 400,
                                                lineHeight: '35.4px',
                                                // text truncates and stays on one line
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: '100%',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontFamily: 'inherit',
                                                    fontWeight: 'inherit',
                                                    lineHeight: 'inherit',
                                                    fontSize: 12.5,
                                                    color: '#0B0B0B',
                                                    flex: 1,
                                                }}
                                            >
                                                Text tool tips
                                            </Typography>
                                            <IconButton
                                                aria-label={isTextHelpOpen ? 'Hide text help' : 'Show text help'}
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsTextHelpOpen((v) => !v);
                                                }}
                                                sx={{
                                                    transform: isTextHelpOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    transition: 'transform 0.2s ease',
                                                    ml: 0.5,
                                                }}
                                            >
                                                <ExpandMoreIcon />
                                            </IconButton>
                                        </Box>

                                        {/* Collapsible help content */}
                                        <Collapse in={isTextHelpOpen} timeout="auto" unmountOnExit>
                                            <Box
                                                sx={{
                                                    pt: 1,
                                                    borderTop: '1px solid #E6E6E6',
                                                    fontFamily: 'Inter, sans-serif',
                                                    maxHeight: 240,
                                                    overflowY: 'auto',
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                                                    <strong>1. Add:</strong> Select <em>Text</em> tool → click on canvas
                                                    → type
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                                                    <strong>2. Edit:</strong> Click a text box → press the{' '}
                                                    <EditOutlinedIcon
                                                        sx={{ fontSize: 16, mx: 0.5, verticalAlign: 'middle' }}
                                                    />
                                                    button (bottom right of illustration) → text is highlighted for
                                                    editing
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                                                    <strong>3. Delete:</strong> Select the text → press Delete or
                                                    Backspace (on mobile use Eraser tool)
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, mb: 0.5 }}>
                                                    <strong>4. Resize:</strong> Select the text → drag a corner point →
                                                    press Esc to finish
                                                </Typography>
                                                <Typography sx={{ fontSize: 13 }}>
                                                    <strong>5. Rotate:</strong> Select text → drag the round point above
                                                    it to rotate freely
                                                </Typography>
                                            </Box>
                                        </Collapse>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            ) : (
                // Collapsed view – icons only
                <Box
                    sx={{
                        width: isLandscape ? '100%' : 260,
                        display: 'flex',
                        justifyContent: isLandscape ? 'flex-start' : 'center',
                    }}
                >
                    <Box
                        className="gradient-icon-background"
                        sx={{
                            display: 'flex',
                            justifyContent: isLandscape ? 'flex-start' : 'center',
                            borderRadius: '16px',
                            padding: '6px',
                            gap: 1,
                            background: 'linear-gradient(132deg, #fa96c3 -2.07%, #ff669b 15.82%, #bd8de8 116.64%)',
                        }}
                    >
                        {[
                            /* Collapsed buttons */
                            { value: 'pencil', icon: MarkerIcon, alt: 'Pencil' },
                            { value: 'highlighter', icon: HighlighterIcon, alt: 'Highlighter' },
                            { value: 'spray', icon: SprayIcon, alt: 'Spray' },
                            { value: 'text', icon: TextIcon, alt: 'Text' },
                            { value: 'eraser', icon: EraserIcon, alt: 'Eraser' },
                        ].map((tool) => (
                            <ToggleButton
                                key={tool.value}
                                value={tool.value}
                                selected={activeTool === tool.value}
                                onClick={() => {
                                    setActiveTool(tool.value);
                                    setIsExpanded(true);
                                }}
                                sx={{
                                    background: 'transparent',
                                    border: 'none',
                                    borderRadius: 2,
                                    padding: '6px',
                                    '&.Mui-selected': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    },
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    },
                                }}
                            >
                                <img src={tool.icon} alt={tool.alt} style={{ width: 24, height: 24 }} />
                            </ToggleButton>
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default WhiteboardToolbar;
